import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, ReceiptTextIcon } from 'lucide-react';
import { Panel, PanelEmpty, rowClass } from './Panel';
import { dayDiff, formatKr, relativeAgo } from '../../../utils/adminDates';
import type { Invoice } from '../../../types/invoice';

/** Grøn = betalt, gul = afventer betaling, rød = overskredet. */
function paymentState(invoice: Invoice): {
  text: string;
  tone: string;
} {
  if (invoice.status === 'Betalt') {
    return {
      text: invoice.paidAt ? `Betalt ${relativeAgo(invoice.paidAt).toLowerCase()}` : 'Betalt',
      tone: 'border-status-ok/40 bg-status-ok/10 text-status-ok'
    };
  }
  if (invoice.status === 'Sendt') {
    const days = dayDiff(invoice.dueDate);
    if (days !== null && days < 0) {
      return {
        text: `Forfalden ${-days} ${-days === 1 ? 'dag' : 'dage'}`,
        tone: 'border-danger/40 bg-danger/10 text-danger'
      };
    }
    return {
      text: days === null ? 'Afventer betaling' : days === 0 ? 'Forfalder i dag' : `Forfalder om ${days} ${days === 1 ? 'dag' : 'dage'}`,
      tone: 'border-status-warn/40 bg-status-warn/10 text-status-warn'
    };
  }
  if (invoice.status === 'Klar til afsendelse') {
    return {
      text: 'Klar til afsendelse',
      tone: 'border-accent/40 bg-accent/10 text-accent-deep'
    };
  }
  return {
    text: invoice.status,
    tone: 'border-line bg-canvas-deep text-ink-muted'
  };
}
interface Props {
  invoices: Invoice[];
  titleFor: (invoice: Invoice) => string;
}
export function InvoicesPanel({
  invoices,
  titleFor
}: Props) {
  return <Panel title="Fakturaer" linkLabel="Se alle" linkTo="/admin/fakturaer" data-id="element-314" data-mp="2htfc80uncr6m">
      {invoices.length === 0 ? <PanelEmpty data-id="element-315" data-mp="2msrwqey1pm61">Der er ingen fakturaer endnu.</PanelEmpty> : <ul className="divide-y divide-line/70" data-id="element-316" data-mp="2rmmehcu3br2h">
          {invoices.map(invoice => {
        const state = paymentState(invoice);
        return <li key={invoice.id} data-id="element-317" data-mp="1z48nnx7695cw">
                <Link to={`/admin/fakturaer/${invoice.id}`} className={rowClass} data-id="element-318" data-mp="1z37e5x5t0qxa">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-canvas text-accent" data-id="element-319" data-mp="32emspcwp67ag">
                    <ReceiptTextIcon className="h-5 w-5" strokeWidth={1.6} aria-hidden="true" data-id="element-320" data-mp="3b887gw8v3ojv" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-321" data-mp="3764mnh0az7lx">
                    <span className="block truncate text-[15px] font-semibold text-ink" data-id="element-322" data-mp="378r3k7932zgt">
                      Faktura {invoice.invoiceNumber}
                    </span>
                    <span className="block truncate text-sm text-ink-muted" data-id="element-323" data-mp="2ejadhg8xrunr">{titleFor(invoice)}</span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-3" data-id="element-324" data-mp="2sj1kgx11cas9">
                    <span className="whitespace-nowrap text-[15px] font-semibold text-ink" data-id="element-325" data-mp="2nl03chn66ljf">
                      {formatKr(invoice.total)}
                    </span>
                    <span className={`inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium ${state.tone}`} data-id="element-326" data-mp="2rwmwylmmukc">
                      {state.text}
                    </span>
                  </span>
                  <ChevronRightIcon className="hidden h-4 w-4 shrink-0 text-ink-faint transition-colors duration-150 group-hover:text-accent sm:block" aria-hidden="true" data-id="element-327" data-mp="1car9j2trp7o" />
                </Link>
              </li>;
      })}
        </ul>}
    </Panel>;
}